import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Card } from '@/components/ui/Card'
import { CenterSpinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { StatsStrip } from '@/components/ui/StatsStrip'
import { Select } from '@/components/ui/Select'
import { useAuth } from '@/lib/auth/AuthProvider'
import { moneyCompact } from '@/lib/format'
import { cn } from '@/lib/utils'

interface ProjectRow {
  projectCode: string
  revenue: number
  cost: number
  margin: number
  marginPercent: number | null
  revenueCount: number
  costCount: number
}

async function fetchPnL(companyId: string, months: number) {
  const t = localStorage.getItem('adaptivo.szamla.token')
  const r = await fetch(`/api/v1/project-pnl?companyId=${companyId}&months=${months}`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  })
  if (!r.ok) throw new Error(`Project P&L hiba: ${r.status}`)
  return (await r.json()) as {
    months: number
    since: string
    items: ProjectRow[]
    totals: { revenue: number; cost: number; margin: number }
  }
}

export function ProjectPnLPage() {
  const { activeCompanyId } = useAuth()
  const [months, setMonths] = useState(6)
  const q = useQuery({
    queryKey: ['project-pnl', activeCompanyId, months],
    queryFn: () => fetchPnL(activeCompanyId!, months),
    enabled: !!activeCompanyId,
  })

  if (!activeCompanyId) return <p className="text-sm text-slate-500">Nincs aktív cég.</p>

  return (
    <div>
      <PageHeader
        accent
        eyebrow="Projekt-szintű eredmény"
        title="Projekt P&L"
        subtitle="Projektenkénti bevétel és költség az utolsó X hónap számláiból. Nyereség/veszteség egy pillantásra."
        actions={
          <Select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="h-9 w-40 text-sm">
            <option value={3}>Utolsó 3 hónap</option>
            <option value={6}>Utolsó 6 hónap</option>
            <option value={12}>Utolsó 12 hónap</option>
            <option value={24}>Utolsó 24 hónap</option>
          </Select>
        }
      />

      {q.data && (
        <StatsStrip
          tiles={[
            {
              label: 'Bevétel',
              value: moneyCompact(q.data.totals.revenue),
              sub: `${q.data.items.reduce((s, i) => s + i.revenueCount, 0)} kimenő számla`,
              icon: <TrendingUp className="h-3.5 w-3.5" />,
              tone: 'accent',
            },
            {
              label: 'Költség',
              value: moneyCompact(q.data.totals.cost),
              sub: `${q.data.items.reduce((s, i) => s + i.costCount, 0)} bejövő számla`,
              icon: <TrendingDown className="h-3.5 w-3.5" />,
              tone: 'default',
            },
            {
              label: 'Margin',
              value: moneyCompact(q.data.totals.margin),
              sub: q.data.totals.revenue > 0
                ? `${((q.data.totals.margin / q.data.totals.revenue) * 100).toFixed(1)}%`
                : '—',
              tone: q.data.totals.margin > 0 ? 'brand' : 'warn',
            },
            {
              label: 'Projektek',
              value: q.data.items.length,
              sub: `${months} hónap ablak`,
            },
          ]}
        />
      )}

      {q.isLoading ? (
        <CenterSpinner />
      ) : !q.data || q.data.items.length === 0 ? (
        <EmptyState
          variant="data"
          title="Még nincs projekt-bontás"
          description="A számláknál állítsd be a projekt-kódot (Projekt / Költséghely mező), és itt automatikusan megjelennek a projektenkénti aggregátumok."
        />
      ) : (
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Projekt</th>
                  <th className="text-right px-3 py-2">Bevétel</th>
                  <th className="text-right px-3 py-2">Költség</th>
                  <th className="text-right px-3 py-2">Margin</th>
                  <th className="text-right px-3 py-2">Margin %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {q.data.items.map((p) => (
                  <tr key={p.projectCode} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                    <td className="px-3 py-2 font-medium">{p.projectCode}</td>
                    <td className="px-3 py-2 text-right num-display">{moneyCompact(p.revenue)}</td>
                    <td className="px-3 py-2 text-right num-display text-slate-600 dark:text-slate-300">{moneyCompact(p.cost)}</td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right num-display font-semibold',
                        p.margin > 0 ? 'text-emerald-600 dark:text-emerald-400' : p.margin < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500',
                      )}
                    >
                      {moneyCompact(p.margin)}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-500">
                      {p.marginPercent != null ? p.marginPercent.toFixed(1) + '%' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
